/**
 * RESPONSIBILITY: fade a section up the first time it comes into view.
 *
 * THE CONTENT WINS OVER THE EFFECT. An entrance that hides what it is animating has to
 * be certain of showing it again, and an IntersectionObserver on its own is not:
 *
 *   · at `threshold: 0.15` a section taller than the window never reaches fifteen per
 *     cent visible, so it never fires at all;
 *   · a jump to an anchor, a flick on a phone or a press of End scrolls a section past
 *     without the browser ever reporting it as intersecting, and once it is above the
 *     window it cannot fire — the reader has to scroll back up to make it appear.
 *
 * Both of those left whole bands of the About page blank. So: any pixel counts rather
 * than fifteen per cent, anything already at or above the fold is shown at once without
 * waiting to be told, and a timer shows it regardless if nothing has fired by then.
 * Missing the animation is a small loss; missing the content is not one worth risking.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Long enough for a real entrance, short enough that nobody reads a blank band. */
const SAFETY_MS = 1200;

export function Reveal({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Already on screen, or already scrolled past: show it now. The observer would not
    // report either of those as a change, because neither is one.
    const top = el.getBoundingClientRect().top;
    if (top < window.innerHeight) { setVisible(true); return; }

    const show = () => setVisible(true);
    const timer = window.setTimeout(show, SAFETY_MS);

    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      show();
      io.disconnect();
      window.clearTimeout(timer);
    }, { threshold: 0, rootMargin: '0px 0px -5% 0px' });

    io.observe(el);
    return () => { io.disconnect(); window.clearTimeout(timer); };
  }, []);

  return (
    <div ref={ref} className={`reveal ${visible ? 'is-visible' : ''} ${className}`.trim()}>
      {children}
    </div>
  );
}
